import { withPendingMessages } from "@/hooks/ai-chat/chat-pending-messages";
import type { QueuedChatMessage } from "@/hooks/ai-chat/useAiChatMessageQueue";
import { ChatQueuedMessageStatus } from "./ChatQueuedMessages";
import { ChatHistoryBoundary, LoadingTimeline } from "./ChatHistoryBoundary";
import {
  estimatedRowHeight,
  timelineRows,
  timelineRowKey,
  isActivityRow,
  isHiddenTimelineItem,
  renderedAnchorElement,
  removedVisibleApproval,
  rowContainsItem,
  type TimelineRow,
  type TimelineAnchor,
} from "@/hooks/ai-chat/chat-timeline-rows";
import { ChatToolGroup } from "@/components/features/ai-chat/ChatToolGroup";
import {
  type ChatTimelineItemProps,
  ChatTimelineItem,
} from "@/components/features/ai-chat/ChatTimelineItem";
import { Button } from "@/components/ui";
import {
  useChatFindNavigation,
  type ChatFindTarget,
} from "@/hooks/ai-chat/useChatFindNavigation";
import { visibleChatAnchor } from "@/hooks/ai-chat/chat-scroll-position";
import { useChatHistoryEdge } from "@/hooks/ai-chat/useChatHistoryEdge";
import { useChatTimelineViewport } from "@/hooks/ai-chat/useChatTimelineViewport";
import type {
  AiChatGuardianDecision,
  AiChatGuardianReview,
  AiChatItemBodyPart,
  AiChatLoadedItemBody,
  AiChatTimelineItem,
  AiChatLayoutItem,
} from "@/lib/types/ai-chat";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ArrowDown, LoaderCircle } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type ChatTimelineProps = {
  onAnswerQuestion?: ChatTimelineItemProps["onAnswerQuestion"];
  queuedMessages?: QueuedChatMessage[];
  onRetryMessage?: (id: string) => void;
  onRemoveQueuedMessage?: (id: string) => void;
  approvalDetails?: Readonly<Record<string, AiChatGuardianReview | undefined>>;
  bodyByItemId?: Readonly<Record<string, AiChatLoadedItemBody | undefined>>;
  decidingApprovalIds?: ReadonlySet<string>;
  emptyState?: ReactNode;
  hasNewer?: boolean;
  hasOlder?: boolean;
  isLoadingInitial?: boolean;
  isLoadingNewer?: boolean;
  isLoadingOlder?: boolean;
  isFetchingTimeline?: boolean;
  loadingApprovalIds?: ReadonlySet<string>;
  loadingBodyIds?: ReadonlySet<string>;
  items: AiChatTimelineItem[];
  layoutItems?: AiChatLayoutItem[];
  onLoadWindow?: (itemId: string) => Promise<void>;
  onAtBottomChange?: (atBottom: boolean) => void;
  onDecideApproval?: (
    runId: string,
    reviewId: string,
    decision: AiChatGuardianDecision,
  ) => Promise<void> | void;
  onJumpToLatest?: () => Promise<boolean> | boolean;
  onLoadApprovalDetail?: (
    runId: string,
    reviewId: string,
  ) => Promise<void> | void;
  onLoadBody?: (
    item: AiChatTimelineItem,
    part: AiChatItemBodyPart,
  ) => Promise<void> | void;
  onLoadNewer?: () => Promise<void> | void;
  onLoadOlder?: () => Promise<void> | void;
  onRevealItem: (itemId: string) => Promise<void>;
  onVisibleMatchCount?: (itemId: string, count: number, query: string) => void;
  revision: number;
  searchQuery?: string;
  searchTarget?: ChatFindTarget;
  threadId: string;
};

const NO_PENDING_MESSAGES: QueuedChatMessage[] = [];

export function ChatTimeline({
  onAnswerQuestion,
  queuedMessages = NO_PENDING_MESSAGES,
  onRetryMessage,
  onRemoveQueuedMessage,
  approvalDetails = {},
  bodyByItemId = {},
  decidingApprovalIds,
  emptyState,
  hasNewer = false,
  hasOlder = false,
  isLoadingInitial = false,
  isLoadingNewer = false,
  isLoadingOlder = false,
  isFetchingTimeline = false,
  loadingApprovalIds,
  loadingBodyIds,
  items: savedItems,
  layoutItems,
  onLoadWindow,
  onAtBottomChange,
  onDecideApproval,
  onJumpToLatest,
  onLoadApprovalDetail,
  onLoadBody,
  onLoadOlder,
  onLoadNewer,
  onRevealItem,
  onVisibleMatchCount,
  revision,
  searchQuery = "",
  searchTarget,
  threadId,
}: ChatTimelineProps) {
  const items = useMemo(
    () => withPendingMessages(savedItems, queuedMessages),
    [savedItems, queuedMessages],
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const newerSentinelRef = useRef<HTMLDivElement>(null);
  const pagingRequestLock = useRef(false);
  const preferredPagingDirection = useRef<"older" | "newer">("older");
  const olderSentinelRef = useRef<HTMLDivElement>(null);
  const prependAnchorRef = useRef<TimelineAnchor | null>(null);
  const previousRowsRef = useRef<TimelineRow[]>([]);
  const visibleAnchorRef = useRef<TimelineAnchor | null>(null);
  const [isJumping, setIsJumping] = useState(false);
  const [windowError, setWindowError] = useState<string>();
  const requestedWindowRef = useRef<string>();
  const navigationRef = useRef(0);
  const latestScrollRef = useRef<() => void>(() => {});
  const latest = layoutItems?.at(-1) ?? items.at(-1);
  const latestSignal = latest ? `${latest.id}:${latest.revision}` : "empty";
  const previousProjection = useRef<TimelineRow[]>([]);
  const rows = useMemo(() => {
    const next = timelineRows(
      items,
      layoutItems,
      approvalDetails,
      previousProjection.current,
    );
    previousProjection.current = next;
    return next;
  }, [items, approvalDetails, layoutItems]);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) =>
      estimatedRowHeight(rows[index], scrollRef.current?.clientWidth ?? 800),
    getItemKey: (index) => timelineRowKey(rows[index]),
    overscan: 8,
  });
  // Preserve the anchor for offscreen rows, but do not move a reader when the
  // visible response grows below the text they are currently reading.
  virtualizer.shouldAdjustScrollPositionOnItemSizeChange = (
    item,
    _delta,
    instance,
  ) => item.end <= (instance.scrollOffset ?? 0);
  const totalSize = virtualizer.getTotalSize();

  const findRowIndex = useCallback(
    (itemId: string) => rows.findIndex((row) => rowContainsItem(row, itemId)),
    [rows],
  );
  const scrollToRow = useCallback(
    (index: number, options: { align: "end" | "start"; behavior: "auto" }) => {
      virtualizer.scrollToIndex(index, options);
    },
    [virtualizer],
  );
  const {
    beginFindNavigation,
    handleScroll,
    hasNewActivity,
    isFollowing,
    isViewportReady,
    scrollToLatest,
    stopFollowing,
  } = useChatTimelineViewport({
    contentHeight: totalSize,
    findRowIndex,
    hasNewer,
    isLoadingInitial,
    latestSignal,
    onAtBottomChange,
    onRevealItem,
    revision,
    rowCount: rows.length,
    scrollRef,
    scrollToRow,
    threadId,
  });
  const scrollToSearchRow = useCallback(
    (index: number) => {
      virtualizer.scrollToIndex(index, {
        align: "center",
        behavior: "smooth",
      });
    },
    [virtualizer],
  );
  const hiddenSearchTarget = isHiddenTimelineItem(
    items.find((item) => item.id === searchTarget?.itemId),
    approvalDetails,
  );
  useLayoutEffect(() => {
    if (hiddenSearchTarget && searchTarget) {
      onVisibleMatchCount?.(searchTarget.itemId, 0, searchTarget.query);
    }
  }, [hiddenSearchTarget, onVisibleMatchCount, searchTarget]);
  useChatFindNavigation({
    expandItem: () => {},
    findRowIndex,
    onNavigate: beginFindNavigation,
    onVisibleMatchCount,
    query: searchQuery,
    scrollRef,
    scrollToRow: scrollToSearchRow,
    target: hiddenSearchTarget ? undefined : searchTarget,
  });
  useLayoutEffect(() => {
    if (searchQuery) {
      navigationRef.current += 1;
      setIsJumping(false);
      beginFindNavigation();
    }
  }, [beginFindNavigation, searchQuery, searchTarget]);

  const handleLoadPage = useCallback(
    async (loadPage: (() => Promise<void> | void) | undefined) => {
      const element = scrollRef.current;
      if (!element || !loadPage || isFetchingTimeline) return;
      stopFollowing();
      const renderedAnchor = visibleChatAnchor(element);
      if (renderedAnchor) prependAnchorRef.current = renderedAnchor;
      else {
        const visibleRow = virtualizer
          .getVirtualItems()
          .find((virtualItem) => virtualItem.end > element.scrollTop);
        const row = visibleRow ? rows[visibleRow.index] : undefined;
        if (visibleRow && row) {
          prependAnchorRef.current = {
            itemId: row.id,
            viewportOffset: visibleRow.start - element.scrollTop,
          };
        }
      }
      try {
        await loadPage();
      } catch (error) {
        prependAnchorRef.current = null;
        throw error;
      }
    },
    [isFetchingTimeline, rows, virtualizer, stopFollowing],
  );

  const handleJumpToLatest = useCallback(async () => {
    const navigation = ++navigationRef.current;
    prependAnchorRef.current = null;
    requestedWindowRef.current = undefined;
    stopFollowing();
    setIsJumping(true);
    setWindowError(undefined);
    try {
      const committed = await onJumpToLatest?.();
      if (committed !== false && navigation === navigationRef.current)
        requestAnimationFrame(() => {
          if (navigation === navigationRef.current) latestScrollRef.current();
        });
    } catch {
      if (navigation === navigationRef.current)
        setWindowError("The latest messages could not be loaded. Try again.");
    } finally {
      if (navigation === navigationRef.current) setIsJumping(false);
    }
  }, [onJumpToLatest, stopFollowing]);
  latestScrollRef.current = scrollToLatest;

  const oldestItemId = items[0]?.id;
  const pagingEnabled =
    isViewportReady && !searchQuery && !layoutItems && !isJumping;
  const olderMessages = useChatHistoryEdge({
    enabled: pagingEnabled && hasOlder && Boolean(onLoadOlder),
    isLoading: isFetchingTimeline,
    direction: "older",
    requestLock: pagingRequestLock,
    preferredDirection: preferredPagingDirection,
    boundaryItemId: oldestItemId,
    onLoad: () => handleLoadPage(onLoadOlder),
    scrollRef,
    sentinelRef: olderSentinelRef,
  });
  const newerMessages = useChatHistoryEdge({
    enabled: pagingEnabled && hasNewer && Boolean(onLoadNewer),
    isLoading: isFetchingTimeline,
    direction: "newer",
    requestLock: pagingRequestLock,
    preferredDirection: preferredPagingDirection,
    boundaryItemId: items.at(-1)?.id,
    onLoad: () => handleLoadPage(onLoadNewer),
    scrollRef,
    sentinelRef: newerSentinelRef,
  });

  useLayoutEffect(() => {
    const removedApproval = removedVisibleApproval(
      previousRowsRef.current,
      rows,
    );
    const membershipChanged = previousRowsRef.current[0]?.id !== rows[0]?.id;
    if (
      (removedApproval || membershipChanged) &&
      isViewportReady &&
      !isFollowing &&
      !searchQuery &&
      !isJumping
    ) {
      prependAnchorRef.current ??= visibleAnchorRef.current;
    }
    previousRowsRef.current = rows;
  }, [isFollowing, isViewportReady, rows, searchQuery, isJumping]);

  useLayoutEffect(() => {
    const anchor = prependAnchorRef.current;
    const element = scrollRef.current;
    if (!anchor || !element || isFetchingTimeline || isJumping) return;
    const navigation = navigationRef.current;

    const anchorRowIndex = rows.findIndex((row) =>
      rowContainsItem(row, anchor.itemId),
    );
    if (anchorRowIndex < 0) {
      prependAnchorRef.current = null;
      return;
    }
    virtualizer.scrollToIndex(anchorRowIndex, {
      align: "start",
      behavior: "auto",
    });

    const refineAnchor = () => {
      if (navigation !== navigationRef.current) return;
      const renderedRow = renderedAnchorElement(element, anchor.itemId);
      if (!renderedRow) return;
      const currentOffset =
        renderedRow.getBoundingClientRect().top -
        element.getBoundingClientRect().top;
      element.scrollTop += currentOffset - anchor.viewportOffset;
      prependAnchorRef.current = null;
    };
    let secondFrame: number | undefined;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(refineAnchor);
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame !== undefined) cancelAnimationFrame(secondFrame);
    };
  }, [isFetchingTimeline, oldestItemId, rows, virtualizer, isJumping]);

  useLayoutEffect(() => {
    if (!isViewportReady) return;
    let secondFrame: number | undefined;
    const frame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        if (scrollRef.current)
          visibleAnchorRef.current =
            visibleChatAnchor(scrollRef.current) ?? null;
      });
    });
    return () => {
      cancelAnimationFrame(frame);
      if (secondFrame !== undefined) cancelAnimationFrame(secondFrame);
    };
  }, [isViewportReady, rows]);

  const virtualItems = virtualizer.getVirtualItems();
  const windowTarget = virtualItems
    .map((entry) => rows[entry.index])
    .find((row) => !row.item && !isActivityRow(row));
  const windowTargetId = windowTarget?.id;
  const windowTargetKey = windowTarget
    ? `${windowTarget.id}:${windowTarget.layout.revision}`
    : undefined;
  useEffect(() => {
    if (
      !layoutItems ||
      !onLoadWindow ||
      !windowTargetId ||
      isJumping ||
      searchQuery ||
      isFetchingTimeline ||
      requestedWindowRef.current === windowTargetKey
    )
      return;
    const navigation = navigationRef.current;
    requestedWindowRef.current = windowTargetKey;
    setWindowError(undefined);
    void onLoadWindow(windowTargetId).catch(() => {
      if (navigation === navigationRef.current)
        setWindowError("This part of the conversation could not be loaded.");
    });
  }, [
    layoutItems,
    onLoadWindow,
    windowTargetId,
    windowTargetKey,
    isJumping,
    searchQuery,
    isFetchingTimeline,
  ]);
  const timelineContent = virtualItems.map((virtualItem) => {
    const row = rows[virtualItem.index];
    const item = row.item;
    const activity = isActivityRow(row);
    const placeholder = !item && !activity;
    const reviewId = item?.guardianReviewId;
    const queued = queuedMessages.find(
      (message) => item?.id === `pending:${message.id}`,
    );
    return (
      <div
        key={timelineRowKey(row)}
        data-index={virtualItem.index}
        data-timeline-item-ids={row.anchorItemIds.join(" ")}
        ref={placeholder ? undefined : virtualizer.measureElement}
        className="absolute top-0 left-0 w-full"
        style={{ transform: `translateY(${virtualItem.start}px)` }}
      >
        {placeholder ? (
          <div style={{ height: virtualItem.size }} aria-hidden="true" />
        ) : activity ? (
          <ChatToolGroup
            activity={row.layout.activity}
            status={row.layout.status}
          />
        ) : item ? (
          <ChatTimelineItem
            item={item}
            footer={
              queued ? (
                <ChatQueuedMessageStatus
                  message={queued}
                  onRetry={onRetryMessage}
                  onRemove={onRemoveQueuedMessage}
                />
              ) : undefined
            }
            body={bodyByItemId[item.id]}
            approvalDetail={reviewId ? approvalDetails[reviewId] : undefined}
            isBodyLoading={loadingBodyIds?.has(item.id)}
            isApprovalLoading={
              reviewId ? loadingApprovalIds?.has(reviewId) : false
            }
            isDecidingApproval={
              reviewId ? decidingApprovalIds?.has(reviewId) : false
            }
            onLoadItemBody={onLoadBody}
            onLoadApprovalDetail={onLoadApprovalDetail}
            onDecideApproval={onDecideApproval}
            onAnswerQuestion={onAnswerQuestion}
          />
        ) : null}
      </div>
    );
  });

  return (
    <div className="bg-background relative min-h-0 flex-1 overflow-hidden">
      <div
        ref={scrollRef}
        role="log"
        tabIndex={0}
        aria-label="Conversation"
        aria-busy={isLoadingInitial || !isViewportReady}
        onWheelCapture={(event) => {
          if (event.deltaY < 0) stopFollowing();
        }}
        onKeyDown={(event) => {
          if (
            event.target === event.currentTarget &&
            (["ArrowUp", "PageUp", "Home"].includes(event.key) ||
              (event.key === " " && event.shiftKey))
          ) {
            stopFollowing();
          }
        }}
        onScroll={(event) => {
          const following = handleScroll(event);
          if (!following || isLoadingOlder || isLoadingNewer)
            visibleAnchorRef.current =
              visibleChatAnchor(event.currentTarget) ?? null;
          if ((isLoadingOlder || isLoadingNewer) && prependAnchorRef.current) {
            prependAnchorRef.current = visibleAnchorRef.current;
          }
        }}
        className="h-full min-h-0 overflow-y-auto overscroll-y-contain [overflow-anchor:none]"
      >
        {isLoadingInitial ? (
          <LoadingTimeline />
        ) : !items.length ? (
          <div className="text-muted-foreground flex h-full min-h-72 items-center justify-center px-6 text-center text-sm">
            {emptyState ?? "Start a conversation with LawWorks AI."}
          </div>
        ) : (
          <>
            {!layoutItems &&
              (hasOlder || isLoadingOlder || olderMessages.failed) && (
                <ChatHistoryBoundary
                  sentinelRef={olderSentinelRef}
                  direction="earlier"
                  loading={isLoadingOlder}
                  failed={olderMessages.failed}
                  onRetry={olderMessages.retry}
                />
              )}
            <div
              className="relative w-full"
              style={{
                height: `${totalSize}px`,
                visibility: isViewportReady ? "visible" : "hidden",
              }}
            >
              {timelineContent}
            </div>
            {!layoutItems &&
              (hasNewer || isLoadingNewer || newerMessages.failed) && (
                <ChatHistoryBoundary
                  sentinelRef={newerSentinelRef}
                  direction="newer"
                  loading={isLoadingNewer}
                  failed={newerMessages.failed}
                  onRetry={newerMessages.retry}
                />
              )}
            <div className="h-8" aria-hidden="true" />
          </>
        )}
      </div>
      {windowError && (
        <div
          role="alert"
          className="bg-background/95 absolute top-3 left-1/2 z-20 -translate-x-1/2 rounded-lg border px-3 py-2 text-sm shadow-sm"
        >
          {windowError}{" "}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              requestedWindowRef.current = undefined;
              setWindowError(undefined);
              if (windowTarget && onLoadWindow)
                void onLoadWindow(windowTarget.id).catch(() => {
                  setWindowError(
                    "This part of the conversation could not be loaded.",
                  );
                });
            }}
          >
            Retry
          </Button>
        </div>
      )}
      {isViewportReady && (!isFollowing || hasNewer) && items.length ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="bg-background/95 absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full shadow-sm backdrop-blur"
          disabled={isJumping}
          onClick={() => {
            void handleJumpToLatest();
          }}
        >
          {isJumping ? (
            <LoaderCircle className="animate-spin" />
          ) : (
            <ArrowDown />
          )}
          {isJumping
            ? "Loading latest"
            : hasNewer || hasNewActivity
              ? "New activity"
              : "Jump to latest"}
        </Button>
      ) : null}
    </div>
  );
}
